<template>
    <div id="playlist-editor-view" class="max-w-screen w-full h-full max-h-full relative flex flex-col min-h-0">
        <div data-tour="playlist-header" class="w-full">
            <PageHeader
                :title="playlist.name || t('playlistEditor.loading')"
                :subtitle="`${playlist.songs?.length || 0} ${t('playlistEditor.songs')} &middot; ${t('playlistEditor.lastUpdated')}: ${lastTimeSaved}`"
                :showBack="true"
                @back="$router.back()"
            >
                <div class="flex items-center gap-2 text-sm font-medium transition-colors duration-200" 
                :class="isSaving ? 'text-blue-400' : (isSaved ? 'text-green-400' : 'text-red-400')">
                    <template v-if="isSaving">
                        <svg class="animate-spin -ml-1 mr-1 size-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ t('playlistEditor.saving') }}
                    </template>
                    <template v-else>
                        <CloudArrowUpIcon class="size-5" />
                        {{ isSaved ? t('playlistEditor.saved') : t('playlistEditor.notSaved') }}
                    </template>
                </div>
                <button 
                    v-if="!isSaved && !isSaving" 
                    type="button" 
                    @click="playlistEditorRef?.onManualSave(true)"
                    class="px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white text-xs font-semibold shadow-xs transition-all cursor-pointer flex items-center gap-1 shrink-0">
                    <CloudArrowUpIcon class="size-3.5" />
                    <span>{{ t('notebookPagesEditor.save') || 'Salvar' }}</span>
                </button>
                <div data-tour="playlist-autosave" class="flex items-center gap-3">
                    <label for="auto-save-toggle" class="flex items-center cursor-pointer select-none">
                        <div class="relative">
                            <input type="checkbox" id="auto-save-toggle" class="sr-only" v-model="autoSaveEnabled" @change="toggleAutoSave">
                            <div class="block w-10 h-6 rounded-full transition-colors" :class="autoSaveEnabled ? 'bg-blue-500' : 'bg-gray-400'"></div>
                            <div class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200" :class="{'transform translate-x-4': autoSaveEnabled}"></div>
                        </div>
                        <div class="ml-3 text-sm font-medium text-gray-400">{{ t('playlistEditor.autoSave') }}</div>
                    </label>
                </div>
                <!-- Tour guide trigger button -->
                <button 
                    type="button" 
                    @click="() => startPlaylistEditorTour(true)"
                    class="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer flex items-center justify-center shrink-0 shadow-sm"
                    :title="$t('tour.startTour')">
                    <QuestionMarkCircleIcon class="size-4" />
                </button>
            </PageHeader>
        </div>
        <PlaylistEditor data-tour="playlist-editor-content" ref="playlistEditorRef" class="flex-1 min-h-0"
            :playlistId="playlistId" 
            :autoSave="autoSaveEnabled"
            @update:playlist="onUpdatePlaylist" 
            @saving:playlist="onSavingPlaylist"
            @save:playlist="onSavePlaylist" />
    </div>
</template>

<script setup>
import toastStore from '@/store/toast.store';
import playlistService from '@/service/playlist.service';
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import PlaylistEditor from '@/components/playlist/PlaylistEditor.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import { useRoute } from 'vue-router';
import { CloudArrowUpIcon } from '@heroicons/vue/24/solid';
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline';
import { useTour } from '@/composables/useTour';

const { t } = useI18n();
const route = useRoute();
const { startPlaylistEditorTour } = useTour();
const playlistId = ref(route.params.id);
const playlistEditorRef = ref(null);
const isSaved = ref(false);

const autoSaveEnabled = ref(localStorage.getItem('playlistAutoSave') !== 'false');

const toggleAutoSave = () => {
    localStorage.setItem('playlistAutoSave', autoSaveEnabled.value);
    if (autoSaveEnabled.value && playlistEditorRef.value) {
        playlistEditorRef.value.onManualSave(false);
    }
};

const playlist = ref({
    _id: null,
    name: '',
    songs: []
});

const onUpdatePlaylist = (p) => {
    playlist.value = p;
}
const isSaving = ref(false);
const timeSaved = ref(0);
const updateInterval = ref(null);
const lastTimeSaved = ref('just now');
const updateLabelLastTimeSaved = () => {
    updateInterval.value = setInterval(() => {
        const total = Math.round((Date.now() - timeSaved.value) / 1000);
        if (total <= 10) {
            lastTimeSaved.value = 'just now';
            return;
        }
        if (total < 60) {
            lastTimeSaved.value = total + 's ago';
        } else if (total < 3600) {
            lastTimeSaved.value = Math.round(total / 60) + 'm ago';
        } else {
            lastTimeSaved.value = Math.round(total / 3600) + 'h ago';
        }
    }, 1000);
}
onBeforeUnmount(() => {
    clearInterval(updateInterval.value);
})
const onSavingPlaylist = () => {
    isSaving.value = true;
}

const onSavePlaylist = async (p, doneCallback, skipPut = false) => {
    try{
        isSaving.value = true;
        if (!skipPut) {
            const response = await fetch('/api/playlist/' + p._id, {
                method: 'PUT',
                body: JSON.stringify(p),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                isSaved.value = true;
                timeSaved.value = Date.now();
                updateLabelLastTimeSaved();
                // Fase 2b: snapshot via transport (cobre pares WebRTC/LAN do Go/Ably).
                playlistService.notifyPlaylistChanged(p._id || playlistId.value, p);
                if (typeof doneCallback === 'function') doneCallback();
            } else {
                console.error('Save failed', response.statusText);
                isSaved.value = false;
                toastStore.dispatch('addMessage', {
                    message: `Error saving playlist ${playlist.value.name}`,
                    icon: 'error',
                    ttl: 3000
                })
            }
        } else {
            isSaved.value = true;
            timeSaved.value = Date.now();
            updateLabelLastTimeSaved();
            if (typeof doneCallback === 'function') doneCallback();
        }
    } catch (error) {
        console.error(error);
        toastStore.dispatch('addMessage', {
            message: `Error saving playlist ${playlist.value.name}`,
            icon: 'error',
            ttl: 3000
        });
    } finally {
        isSaving.value = false;
        if (typeof doneCallback === 'function') doneCallback();
    }
}

</script>